"use client";

import type { MotionValue } from "framer-motion";
import { motion } from "framer-motion";
import HeroStorePlaceholder from "@/components/landing/Hero/HeroStorePlaceholder";
import FloatCardIconSvg from "@/components/landing/Hero/svg/FloatCardIconSvg";
import { BrowserFrame, FallbackImage } from "@/components/landing/shared";
import { Logo } from "@/components/Logo";

interface BrowserWindowProps {
	image?: string;
	imageAlt: string;
	locale: string;
	browserX: MotionValue<number>;
	browserY: MotionValue<number>;
	browserRotateX: MotionValue<number>;
	browserRotateY: MotionValue<number>;
}

/**
 * The hero's visual centerpiece: a browser chrome frame floating on its own
 * plane with a static Apple-style tilt plus a couple of degrees of cursor
 * response. Shows the configured hero image, or the placeholder storefront
 * mockup (with its own floating cart badge) when none is set.
 */
function BrowserWindow(prop: BrowserWindowProps) {
	const { image, imageAlt, locale, browserX, browserY, browserRotateX, browserRotateY } = prop;
	return (
		<>
			<motion.div
				className="relative z-10 mx-auto overflow-hidden rounded-[1.6rem] border border-border bg-surface shadow-[0_20px_45px_-20px_rgba(0,0,0,0.35),0_8px_20px_-10px_rgba(0,0,0,0.2)] lg:max-w-xl"
				style={{
					x: browserX,
					y: browserY,
					rotateY: browserRotateY,
					rotateX: browserRotateX,
				}}
			>
				<BrowserFrame size="lg" />
				{image ? (
					<FallbackImage
						src={image}
						alt={imageAlt}
						loading="eager"
						fetchPriority="high"
						className="block aspect-[16/10] w-full object-cover"
						fallback={
							<div className="grid aspect-[16/10] place-items-center">
								<Logo markOnly className="jana-float h-56 w-56 opacity-25" />
							</div>
						}
					/>
				) : (
					<HeroStorePlaceholder locale={locale} />
				)}
			</motion.div>

			{!image && (
				<motion.div
					className="absolute -end-2 -bottom-2 z-20 h-10 w-10 sm:-end-5 sm:-bottom-5 sm:h-12 sm:w-12"
					style={{ x: browserX, y: browserY }}
				>
					<div
						className="jana-float grid h-10 w-10 place-items-center rounded-full text-white shadow-[var(--shadow)] sm:h-12 sm:w-12"
						style={{
							background: "linear-gradient(135deg, var(--brand-1), var(--brand-2))",
							animationDelay: "-3s",
						}}
					>
						<span className="relative">
							<FloatCardIconSvg kind="cart" className="h-5 w-5 sm:h-6 sm:w-6" />
							<span className="absolute -end-2 -top-2 grid h-4 w-4 place-items-center rounded-full bg-white text-[9px] font-bold text-text">
								2
							</span>
						</span>
					</div>
				</motion.div>
			)}
		</>
	);
}

export default BrowserWindow;
